import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { endpoints, type ExchangeAnalytics } from "@/lib/api";
import { fmtDate, fmtNumber, fmtTimestamp } from "@/lib/format";
import { Badge, lifecycleVariant, lifecycleLabel } from "@/components/ui/Badge";
import { PlatformBadge } from "@/components/PlatformBadge";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";

// ---------------------------------------------------------------------------
// v2.2.0 — Exchange dashboard
//
// One competitor (or our self exchange) — show all activity we've recorded
// against this exchange in a chosen time window:
//   - volume + platform breakdown
//   - exposure (total/median views, likes)
//   - content shape (dedicated vs shared roundup)
//   - weekly trend chart
//   - Top KOLs ranking
//   - quality control (confirmed vs pending; rejected = noise filtered)
//
// Pure SQL aggregation, no LLM. v2.2.1 will layer in topic/category
// analysis using LLM on the recent content.
// ---------------------------------------------------------------------------

const DAYS_OPTIONS = [
  { value: 30, label: "30 天" },
  { value: 90, label: "90 天" },
  { value: 180, label: "180 天" },
  { value: 365, label: "1 年" },
];

export default function ExchangeDetail() {
  const { id: idStr } = useParams<{ id: string }>();
  const id = Number(idStr);
  const [days, setDays] = useState(90);

  const exQ = useQuery({
    queryKey: ["exchanges"],
    queryFn: () => endpoints.exchanges(),
    staleTime: 5 * 60_000,
  });
  const ex = (exQ.data ?? []).find((e) => e.id === id);

  const dataQ = useQuery({
    queryKey: ["exchange-analytics", id, days],
    queryFn: () => endpoints.exchangeAnalytics(id, days, 10),
    staleTime: 30_000,
  });

  const data = dataQ.data;

  return (
    <div className="h-full overflow-y-auto bg-muted/30">
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <Link to="/exchanges" className="text-xs text-muted-foreground hover:underline">
            ← 交易所
          </Link>
          <h1 className="text-lg font-semibold">
            {ex?.display_name ?? `Exchange #${id}`}
          </h1>
          {ex?.is_self && <Badge variant="success">Bitbase 自身</Badge>}
          {ex && !ex.is_self && <Badge variant="secondary">竞品</Badge>}
          <Select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="ml-auto w-28"
          >
            {DAYS_OPTIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </Select>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          仅统计 confirmed + pending 推广(rejected 视为噪声单独显示);时间窗按 video.published_at
        </p>
      </div>

      <div className="mx-auto max-w-6xl space-y-4 p-6">
        {dataQ.isLoading && (
          <p className="text-sm text-muted-foreground">加载中…</p>
        )}
        {dataQ.isError && (
          <p className="text-sm text-destructive">
            加载失败:{(dataQ.error as Error).message}
          </p>
        )}
        {data && data.summary.total_sponsorships === 0 && (
          <div className="rounded border border-border bg-background p-6 text-center text-sm text-muted-foreground">
            过去 {days} 天没有针对 <b>{ex?.display_name}</b> 的推广记录。
          </div>
        )}
        {data && data.summary.total_sponsorships > 0 && (
          <>
            <SummaryCards data={data} />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <PlatformBreakdownCard data={data} />
              <ContentShapeCard data={data} />
            </div>
            <WeeklyTrendCard data={data} />
            <InsightsCard exchangeID={id} days={days} />
            <TopChannelsCard data={data} />
            <NoiseCard data={data} />
          </>
        )}
      </div>
    </div>
  );
}

function SummaryCards({ data }: { data: ExchangeAnalytics }) {
  const s = data.summary;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <KPI label="推广总数" value={fmtNumber(s.total_sponsorships)} hint={`涉及 ${s.distinct_videos} 条内容 · ${s.distinct_channels} 个 KOL`} />
      <KPI label="总曝光" value={fmtNumber(s.total_views)} hint={`中位 ${fmtNumber(Math.round(s.median_views))} / 条`} />
      <KPI label="总互动 (likes)" value={fmtNumber(s.total_likes)} />
      <KPI label="平均置信度" value={s.avg_score.toFixed(2)} hint="0-1 score" />
    </div>
  );
}

function KPI({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="rounded border border-border bg-background p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function PlatformBreakdownCard({ data }: { data: ExchangeAnalytics }) {
  const s = data.summary;
  const total = s.total_sponsorships || 1;
  const items: { code: string; count: number }[] = [
    { code: "youtube", count: s.youtube_count },
    { code: "twitter", count: s.twitter_count },
    { code: "tiktok", count: s.tiktok_count },
  ].filter((i) => i.count > 0);
  return (
    <div className="rounded border border-border bg-background p-4">
      <h3 className="mb-3 text-sm font-semibold">平台分布</h3>
      <ul className="space-y-2 text-sm">
        {items.map((i) => (
          <li key={i.code} className="flex items-center gap-2">
            <PlatformBadge platform={i.code} />
            <div className="flex-1">
              <div className="h-2 rounded-full bg-muted">
                <div
                  className="h-2 rounded-full bg-primary"
                  style={{ width: `${(i.count / total) * 100}%` }}
                />
              </div>
            </div>
            <span className="w-16 text-right tabular-nums text-muted-foreground">
              {i.count} ({((i.count / total) * 100).toFixed(0)}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContentShapeCard({ data }: { data: ExchangeAnalytics }) {
  const s = data.summary;
  const total = s.dedicated_count + s.shared_count || 1;
  const dedPct = (s.dedicated_count / total) * 100;
  return (
    <div className="rounded border border-border bg-background p-4">
      <h3 className="mb-3 text-sm font-semibold">内容形态</h3>
      <div className="flex items-center gap-2 text-sm">
        <div className="flex-1">
          <div className="h-3 overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-sky-500" style={{ width: `${dedPct}%` }} />
          </div>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="inline-block h-2 w-2 rounded-full bg-sky-500" /> 独家推广{" "}
          <b className="tabular-nums">{s.dedicated_count}</b>{" "}
          <span className="text-muted-foreground">({dedPct.toFixed(0)}%)</span>
        </div>
        <div>
          <span className="inline-block h-2 w-2 rounded-full bg-muted" /> 共现(汇总/对比){" "}
          <b className="tabular-nums">{s.shared_count}</b>{" "}
          <span className="text-muted-foreground">({(100 - dedPct).toFixed(0)}%)</span>
        </div>
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">
        独家 = 这条内容只提到 {0 === 0 ? "本交易所" : ""}该家交易所;共现 = 跟其它交易所同帖出现(典型:空投/对比汇总)
      </p>
    </div>
  );
}

function WeeklyTrendCard({ data }: { data: ExchangeAnalytics }) {
  const chartData = data.weekly_trend.map((w) => ({
    week: w.week_start.slice(5), // MM-DD
    sponsorships: w.sponsorships,
    views: w.total_views,
  }));
  return (
    <div className="rounded border border-border bg-background p-4">
      <h3 className="mb-3 text-sm font-semibold">每周推广量</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip
              cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
              contentStyle={{
                background: "hsl(var(--background))",
                border: "1px solid hsl(var(--border))",
                fontSize: 12,
              }}
            />
            <Bar dataKey="sponsorships" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TopChannelsCard({ data }: { data: ExchangeAnalytics }) {
  return (
    <div className="rounded border border-border bg-background p-4">
      <h3 className="mb-3 text-sm font-semibold">
        Top KOL 排行 (按推广次数)
      </h3>
      {data.top_channels.length === 0 ? (
        <p className="text-xs text-muted-foreground">暂无</p>
      ) : (
        <ul className="space-y-2">
          {data.top_channels.map((c, idx) => (
            <li
              key={c.channel_id}
              className="flex items-center gap-3 rounded border border-border bg-muted/20 p-2 text-sm"
            >
              <span className="w-6 text-right text-xs text-muted-foreground tabular-nums">
                #{idx + 1}
              </span>
              <PlatformBadge platform={c.platform} />
              <Link
                to={`/channels/${c.channel_id}`}
                className="font-medium text-primary hover:underline"
              >
                {c.name || c.handle || `#${c.channel_id}`}
              </Link>
              <Badge variant={lifecycleVariant(c.lifecycle_status)} className="text-[10px]">
                {lifecycleLabel(c.lifecycle_status)}
              </Badge>
              <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                <b className="text-foreground">{c.sponsorships}</b> 条 · {fmtNumber(c.total_views)} 观看
                {c.latest_at && <span> · 最近 {fmtDate(c.latest_at)}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// v2.2.2 — LLM insights card. Generation is async on the server:
// POST /refresh returns 202 immediately; this card polls GET /insights
// every 2s while the response's `is_generating` flag is true, then
// stops polling and renders the cached payload. Failures are surfaced
// via the audit log (next GET shows old cached data + a stale badge).
function InsightsCard({ exchangeID, days }: { exchangeID: number; days: number }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["exchange-insights", exchangeID, days],
    queryFn: () => endpoints.exchangeInsights(exchangeID, days),
    staleTime: 60_000,
    // Poll every 2s while the backend reports generation in progress.
    // Once it flips back to false (success or failure), the next GET
    // returns the cached payload and polling stops.
    refetchInterval: (q) => (q.state.data?.is_generating ? 2000 : false),
  });
  const refreshMut = useMutation({
    mutationFn: () => endpoints.refreshExchangeInsights(exchangeID, days),
    onSuccess: () => {
      // Server now generating in background — invalidate so the poll
      // kicks in (the next GET will report is_generating=true).
      qc.invalidateQueries({ queryKey: ["exchange-insights", exchangeID, days] });
    },
  });
  const data = q.data;
  const generating = !!data?.is_generating || refreshMut.isPending;
  const hasCached = !!data?.insights;
  return (
    <div className="rounded border border-border bg-background p-4">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-sm font-semibold">🤖 内容洞察 (LLM)</h3>
        {hasCached && data?.generated_at && (
          <span className="text-[11px] text-muted-foreground">
            生成 {fmtTimestamp(data.generated_at)} · {data.model} · {data.content_count} 样本 · {(data.input_tokens ?? 0) + (data.output_tokens ?? 0)} tokens
          </span>
        )}
        {data?.is_stale && !generating && (
          <Badge variant="warning" className="text-[10px]">
            缓存已过期
          </Badge>
        )}
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          disabled={generating}
          onClick={() => refreshMut.mutate()}
        >
          {generating
            ? `生成中…${data?.generating_for ? ` (${data.generating_for})` : ""}`
            : hasCached
            ? "重新生成"
            : "生成洞察"}
        </Button>
      </div>
      {q.isLoading && <p className="text-xs text-muted-foreground">加载缓存中…</p>}
      {!q.isLoading && !hasCached && !generating && (
        <p className="text-xs text-muted-foreground">
          这个时间窗还没有缓存的洞察。点击右上角"生成洞察"调 LLM 分析。
        </p>
      )}
      {!q.isLoading && !hasCached && generating && (
        <p className="text-xs text-muted-foreground">
          LLM 正在分析(通常 5-15 秒,首次刷新可能更慢),会自动刷新结果…
        </p>
      )}
      {generating && isStuckLLM(data?.generating_for) && (
        // v2.4.7 — escalate visual when generation exceeds 30s. Backend
        // already reports server-side elapsed via generating_for, but the
        // UI showed it inline-bordered; this card surfaces it as a
        // warning + offers a manual "stop polling" so the operator can
        // get the page out of the spinner state. The server-side LLM
        // call still runs to completion (whether to its own 5min
        // timeout or to success) — we're only stopping the client's
        // polling, not cancelling the work.
        <div className="my-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs">
          <div className="font-medium text-amber-900">
            ⚠ 已等待 {data?.generating_for}(预期 5-15 秒)
          </div>
          <div className="mt-1 text-muted-foreground">
            LLM 可能卡在外部 API。继续后台等待,或者:
          </div>
          <button
            className="mt-1 rounded border border-border bg-background px-2 py-0.5 text-xs hover:bg-muted"
            onClick={() => {
              // Re-fetch once explicitly; if it still says is_generating,
              // operator can refresh the page later. Polling stops
              // automatically once is_generating flips to false.
              void q.refetch();
            }}
          >
            手动检查一次
          </button>
        </div>
      )}
      {refreshMut.isError && (
        <p className="text-xs text-destructive">
          触发失败:{(refreshMut.error as Error).message}
        </p>
      )}
      {data?.last_error && !generating && (
        <div className="mb-2 rounded border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs">
          <div className="font-medium text-destructive">
            ⚠ 上次刷新失败{data.last_error_at && ` (${fmtTimestamp(data.last_error_at)})`}
          </div>
          <div className="mt-1 text-muted-foreground break-all">{data.last_error}</div>
        </div>
      )}
      {hasCached && data?.insights && (
        <div className="space-y-3">
          <div>
            <div className="text-xs font-medium text-muted-foreground">总结</div>
            <p className="mt-1 text-sm">{data.insights.summary}</p>
            {data.insights.notes && (
              <p className="mt-1 text-xs italic text-amber-700">
                ⚠ {data.insights.notes}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <div className="mb-2 text-xs font-medium text-muted-foreground">
                主要话题(按出现频次)
              </div>
              <ul className="space-y-1.5">
                {data.insights.top_topics.map((t, i) => (
                  <li key={i} className="text-sm">
                    <span className="inline-block min-w-[1.5rem] text-xs text-muted-foreground tabular-nums">
                      #{i + 1}
                    </span>
                    <b>{t.topic}</b>{" "}
                    <span className="text-xs text-muted-foreground">×{t.count}</span>
                    {t.example && (
                      <div className="ml-6 mt-0.5 text-[11px] italic text-muted-foreground line-clamp-1">
                        “{t.example}”
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="mb-2 text-xs font-medium text-muted-foreground">
                推广类型分布
              </div>
              <ul className="space-y-1">
                {data.insights.promotion_types.map((t, i) => (
                  <li key={i} className="text-sm">
                    <Badge variant="secondary" className="mr-2 text-[10px]">
                      {promotionTypeLabel(t.type)}
                    </Badge>
                    <span className="tabular-nums text-muted-foreground">×{t.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PROMO_TYPE_ZH: Record<string, string> = {
  welcome_bonus: "新人福利",
  trading_competition: "交易大赛",
  airdrop: "空投活动",
  educational: "教学/测评",
  brand_mention: "品牌提及",
  other: "其它",
};
// isStuckLLM returns true when the server-reported generating_for elapsed
// time is past 30 seconds. v2.4.7 — used by the insights card to escalate
// the spinner-ish "生成中" state into an explicit "可能卡住" warning.
// generating_for is a Go time.Duration.String() value: "12.34s", "1m30s",
// "5m0s" — we parse the first number + unit.
function isStuckLLM(generatingFor: string | undefined): boolean {
  if (!generatingFor) return false;
  // Match leading float + optional unit.
  const m = /^(\d+(?:\.\d+)?)(m|s|h)/.exec(generatingFor);
  if (!m) return false;
  const n = parseFloat(m[1]);
  if (m[2] === "h") return true;
  if (m[2] === "m") return n >= 1; // any minute count >= 1 already past 30s
  return n >= 30;
}

function promotionTypeLabel(t: string) {
  return PROMO_TYPE_ZH[t] ?? t;
}

function NoiseCard({ data }: { data: ExchangeAnalytics }) {
  const s = data.summary;
  return (
    <div className="rounded border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
      <b className="text-foreground">质量控制</b>:本期共检测 {s.confirmed_count + s.pending_count + s.rejected_count} 条候选——
      其中 <b>{s.confirmed_count}</b> 已确认,<b>{s.pending_count}</b> 待审,
      <b> {s.rejected_count}</b> 被驳回(噪声/误判,未计入上面统计)
    </div>
  );
}
